import type { IDatasource, IGetRowsParams } from "ag-grid-community";
import { format } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { to12HourTime } from "@/components/news-alerts/shared/utils";
import type { BriefingsStatusFilter } from "@/components/scheduled-briefings/briefings-index/BriefingsIndexContentSection";
import {
  type BriefingTableRow as BriefingTableGraphQLRow,
  type BriefingTableOrdering,
  type BriefingTableTabCounts,
  GetBriefingsTable,
  type GetBriefingsTableVariables,
} from "@/data-access/scheduled-briefings/queries/briefing-table-queries";
import { client } from "@/lib/urqlProvider";
import {
  type BriefingsTableRecipient,
  type BriefingsTableRow,
  type BriefingsTableStatus,
  IS_NEEDS_REVIEW_ENABLED,
} from "./briefingsTable.types";

const DEFAULT_PAGE_SIZE = 50;
const MISSING_TEMPLATE_LABEL = "—";
const LAST_GENERATED_FORMAT = "MMM d, h:mm a";

export interface FetchBriefingsTableFilters {
  searchMatch?: string;
  statusTab: BriefingsStatusFilter;
}

export interface BriefingsTableDatasourceCallbacks {
  setIsLoading: (isLoading: boolean) => void;
  setError: (message: string | null) => void;
  onPageLoaded: (payload: {
    totalCount: number;
    tabCounts: BriefingTableTabCounts;
  }) => void;
}

const SEND_ON_LABELS: Record<string, string> = {
  DAILY: "Daily",
  MONDAY: "Mondays",
  TUESDAY: "Tuesdays",
  WEDNESDAY: "Wednesdays",
  THURSDAY: "Thursdays",
  FRIDAY: "Fridays",
  SATURDAY: "Saturdays",
  SUNDAY: "Sundays",
  WEEKDAYS: "Weekdays",
  MONTHLY: "Monthly",
};

const TIMEZONE_ENUM_SEPARATORS = ["/", "_", "-"];
const ianaTimezoneByEnum = new Map<string, string | undefined>();

const resolveIanaTimezone = (candidate: string): string | undefined => {
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: candidate,
    }).resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
};

/**
 * Resolves a backend timezone enum to an IANA zone Intl accepts, including legacy links, e.g.
 * `AMERICA_PORT_AU_PRINCE` → `America/Port-au-Prince`, `US_CENTRAL` → `US/Central`, `ETC_GMT_MINUS_5` → `Etc/GMT-5`.
 * The enum flattens `/`, `_` and `-` into `_`, so each `_` is tried as each separator.
 */
const ianaTimezoneFromEnum = (timezone: string): string | undefined => {
  if (ianaTimezoneByEnum.has(timezone)) {
    return ianaTimezoneByEnum.get(timezone);
  }
  const [first = "", ...rest] = timezone
    .replace(/_MINUS_/g, "-")
    .replace(/_PLUS_/g, "+")
    .replace(/_(\d+)$/, "+$1")
    .split("_");
  const candidates = rest.reduce(
    (prefixes, token) =>
      prefixes.flatMap((prefix) =>
        TIMEZONE_ENUM_SEPARATORS.map((separator) => prefix + separator + token),
      ),
    [first],
  );
  let ianaTimezone: string | undefined;
  for (const candidate of candidates) {
    ianaTimezone = resolveIanaTimezone(candidate);
    if (ianaTimezone) {
      break;
    }
  }
  ianaTimezoneByEnum.set(timezone, ianaTimezone);
  return ianaTimezone;
};

const initialsFromEmail = (email: string): string => {
  const localPart = email.split("@")[0] ?? email;
  const segments = localPart.split(/[._-]+/).filter(Boolean);
  if (segments.length >= 2) {
    const first = segments[0]?.[0] ?? "";
    const second = segments[1]?.[0] ?? "";
    return `${first}${second}`.toUpperCase();
  }
  return localPart.slice(0, 2).toUpperCase();
};

const mapRecipients = (emails: readonly string[]): BriefingsTableRecipient[] =>
  emails.map((email) => ({
    id: email,
    initials: initialsFromEmail(email),
  }));

const mapStoredStatusToTableStatus = (
  row: BriefingTableGraphQLRow,
): BriefingsTableStatus =>
  IS_NEEDS_REVIEW_ENABLED && row.needsReview ? "NEEDS_REVIEW" : row.status;

const formatBriefingTableScheduleLabel = (
  row: Pick<BriefingTableGraphQLRow, "sendOn" | "sendAt">,
  ianaTimezone: string | undefined,
): string => {
  const dayLabel = SEND_ON_LABELS[row.sendOn] ?? row.sendOn;
  const timeLabel = to12HourTime(row.sendAt);
  if (!ianaTimezone) {
    return `${dayLabel} • ${timeLabel}`;
  }
  return `${dayLabel} • ${timeLabel} ${formatInTimeZone(new Date(), ianaTimezone, "zzz")}`;
};

const formatBriefingTableLastGeneratedLabel = (
  lastRunDate: string | null | undefined,
  ianaTimezone: string | undefined,
): string => {
  if (!lastRunDate) {
    return "—";
  }
  const date = new Date(lastRunDate);
  return ianaTimezone
    ? formatInTimeZone(date, ianaTimezone, `${LAST_GENERATED_FORMAT} zzz`)
    : format(date, LAST_GENERATED_FORMAT);
};

/** Maps a GraphQL {@link BriefingTableGraphQLRow} to grid {@link BriefingsTableRow}. */
export const mapBriefingTableGraphQLRowToGridRow = (
  row: BriefingTableGraphQLRow,
): BriefingsTableRow => {
  const ianaTimezone = ianaTimezoneFromEnum(row.timezone);
  return {
    id: row.id,
    title: row.name,
    schedule: formatBriefingTableScheduleLabel(row, ianaTimezone),
    recipients: mapRecipients(row.recipients),
    status: mapStoredStatusToTableStatus(row),
    lastGenerated: formatBriefingTableLastGeneratedLabel(
      row.lastRunDate,
      ianaTimezone,
    ),
    templateName: row.template.name || MISSING_TEMPLATE_LABEL,
    ownerName: row.updatedBy.name,
  };
};

export const buildBriefingsTableGraphQLFilters = (
  filters: FetchBriefingsTableFilters,
): GetBriefingsTableVariables["filters"] => {
  const trimmedSearch = filters.searchMatch?.trim();
  const searchFilter = trimmedSearch ? { searchMatch: trimmedSearch } : {};

  if (filters.statusTab === "all") {
    return searchFilter;
  }
  if (filters.statusTab === "NEEDS_REVIEW") {
    return { ...searchFilter, needsReview: true };
  }
  return { ...searchFilter, status: filters.statusTab };
};

const fetchBriefingsTablePage = async (
  filters: FetchBriefingsTableFilters,
  ordering: BriefingTableOrdering | undefined,
  limit: number,
  offset: number,
): Promise<{
  rows: BriefingTableGraphQLRow[];
  totalCount: number;
  tabCounts: BriefingTableTabCounts;
}> => {
  const result = await client
    .query(
      GetBriefingsTable,
      {
        filters: buildBriefingsTableGraphQLFilters(filters),
        ordering: ordering ? [ordering] : undefined,
        pagination: { limit, offset },
      },
      { requestPolicy: "network-only" },
    )
    .toPromise();

  if (result.error) {
    throw new Error(result.error.message);
  }

  const data = result.data?.briefings;

  if (!data) {
    return {
      rows: [],
      totalCount: 0,
      tabCounts: {
        allCount: 0,
        needsReviewCount: 0,
        activeCount: 0,
        pausedCount: 0,
        scheduledCount: 0,
      },
    };
  }

  return {
    rows: data.results ?? [],
    totalCount: data.totalCount ?? 0,
    tabCounts: {
      allCount: data.allCount,
      needsReviewCount: data.needsReviewCount,
      activeCount: data.activeCount,
      pausedCount: data.pausedCount,
      scheduledCount: data.scheduledCount,
    },
  };
};

let currentSessionId = 0;
let inFlightCountForCurrentSession = 0;

/** Call when search, tab, or sort changes so in-flight responses from the previous session are ignored. */
export const startNewBriefingsTableDatasourceSession = () => {
  currentSessionId += 1;
  inFlightCountForCurrentSession = 0;
};

/**
 * Creates the AG Grid infinite row model datasource for the briefings index table.
 * `callbacks` fire only for requests from the current session (including concurrent sibling
 * blocks); `successCallback`/`failCallback` always run so AG Grid can settle every block.
 */
export const createBriefingsTableDatasource = (
  getFilters: () => FetchBriefingsTableFilters,
  getOrdering: () => BriefingTableOrdering | undefined,
  callbacks: BriefingsTableDatasourceCallbacks,
): IDatasource => ({
  getRows: async (params: IGetRowsParams) => {
    const sessionId = currentSessionId;
    inFlightCountForCurrentSession += 1;

    const filters = getFilters();
    const ordering = getOrdering();

    const startRow = params.startRow ?? 0;
    const endRow = params.endRow ?? 0;
    const limit = endRow > startRow ? endRow - startRow : DEFAULT_PAGE_SIZE;
    const offset = startRow;

    const finishLoadingForSession = () => {
      inFlightCountForCurrentSession -= 1;
      if (inFlightCountForCurrentSession === 0) {
        callbacks.setIsLoading(false);
      }
    };

    try {
      callbacks.setIsLoading(true);
      callbacks.setError(null);

      const { rows, totalCount, tabCounts } = await fetchBriefingsTablePage(
        filters,
        ordering,
        limit,
        offset,
      );

      const rowData = rows.map(mapBriefingTableGraphQLRowToGridRow);

      if (sessionId === currentSessionId) {
        callbacks.onPageLoaded({ totalCount, tabCounts });
        finishLoadingForSession();
      }
      params.successCallback(rowData, totalCount);
    } catch (error: unknown) {
      if (sessionId === currentSessionId) {
        callbacks.setError(
          error instanceof Error ? error.message : "Unknown error",
        );
        finishLoadingForSession();
      }
      params.failCallback();
    }
  },
});
